import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FolderOpen, Check, Plus, Settings, ChevronDown } from 'lucide-react'
import { toast } from 'sonner'
import { usePortfolio } from '@/store/usePortfolio'
import { portfoliosAPI } from '@/features/portfolios/api/api'
import { invalidateAssets } from '@/store/assetsSlice'
import { invalidatePrices } from '@/store/pricesSlice'
import { useAppDispatch } from '@/store/hooks'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export const PortfolioSwitcher: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { ownPortfolios, sharedPortfolios, activeId, activeName, activeBaseCurrency, setActive, refetch, invalidate } = usePortfolio()
  const [open, setOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [baseCurrency, setBaseCurrency] = useState<'VND' | 'USD'>('VND')
  const [submitting, setSubmitting] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSelect = async (id: number) => {
    if (id === activeId) { setOpen(false); return }
    dispatch(invalidateAssets())
    dispatch(invalidatePrices())
    setActive(id)
    setOpen(false)
    const isOwn = ownPortfolios.some((p) => p.id === id)
    if (isOwn) {
      // Own → đặt default BE, xoá flag shared localStorage
      localStorage.removeItem('assets_shared_portfolio_id')
      try {
        await portfoliosAPI.update(id, { is_default: 1 })
        invalidate()
        refetch()
      } catch { /* silent */ }
    } else {
      // Shared → không thể đặt default BE, nhớ qua localStorage để F5 còn lại
      localStorage.setItem('assets_shared_portfolio_id', String(id))
    }
  }

  const handleCreate = async () => {
    if (!name.trim()) return
    setSubmitting(true)
    try {
      const res = await portfoliosAPI.create({ name: name.trim(), description: description.trim() || null, base_currency: baseCurrency })
      if (res.success) {
        toast.success('Tạo danh mục thành công')
        setCreateOpen(false)
        setName('')
        setDescription('')
        setBaseCurrency('VND')
        invalidate()
        refetch()
        setActive(res.data.id)
        dispatch(invalidateAssets())
        dispatch(invalidatePrices())
      } else {
        toast.error(res.message || 'Tạo thất bại')
      }
    } catch {
      toast.error('Lỗi khi tạo danh mục')
    } finally {
      setSubmitting(false)
    }
  }

  const label = activeName || 'Chọn danh mục'

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
      >
        <FolderOpen className="w-4 h-4 text-gray-500" />
        <span className="max-w-[100px] sm:max-w-[180px] truncate">{label}</span>
        {activeName && (
          <span className="text-[10px] px-1 py-0.5 rounded bg-gray-200 text-gray-600 font-mono">{activeBaseCurrency}</span>
        )}
        <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-[calc(100vw-2rem)] sm:w-72 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50 animate-fadeIn max-h-80 overflow-y-auto">
          {/* Own portfolios */}
          {ownPortfolios.length > 0 && (
            <>
              <p className="px-4 py-1.5 text-[10px] font-semibold text-gray-400 uppercase">Của tôi</p>
              {ownPortfolios.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleSelect(p.id)}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors group"
                >
                  <FolderOpen className="w-4 h-4 text-indigo-500 shrink-0" />
                  <span className="flex-1 text-left truncate">{p.name}</span>
                  <span className="text-[9px] px-1 py-0.5 rounded bg-gray-100 text-gray-500 font-mono shrink-0">{p.base_currency}</span>
                  {activeId === p.id && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                </button>
              ))}
            </>
          )}

          {/* Shared portfolios */}
          {sharedPortfolios.length > 0 && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <p className="px-4 py-1.5 text-[10px] font-semibold text-gray-400 uppercase">Được chia sẻ</p>
              {sharedPortfolios.map((s) => (
                <button
                  key={`shared-${s.portfolio_id}`}
                  onClick={() => handleSelect(s.portfolio_id)}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FolderOpen className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="flex-1 text-left truncate">{s.portfolio_name}</span>
                  <span className="text-[9px] px-1 py-0.5 rounded bg-gray-100 text-gray-500 font-mono shrink-0">{s.base_currency}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${
                    s.role === 'editor' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {s.role === 'editor' ? 'Biên tập' : 'Xem'}
                  </span>
                  {activeId === s.portfolio_id && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                </button>
              ))}
            </>
          )}

          <div className="border-t border-gray-100 my-1" />

          <button
            onClick={() => { setOpen(false); setCreateOpen(true) }}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-indigo-600 hover:bg-indigo-50 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Tạo danh mục mới</span>
          </button>
          <button
            onClick={() => { setOpen(false); navigate('/portfolios') }}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span>Quản lý danh mục</span>
          </button>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tạo danh mục mới</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Tên danh mục</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ví dụ: Cá nhân, Kinh doanh..."
              />
            </div>
            <div className="grid gap-2">
              <Label>Mô tả</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Mô tả ngắn (tuỳ chọn)"
              />
            </div>
            <div className="grid gap-2">
              <Label>Đơn vị tiền tệ</Label>
              <div className="flex gap-2">
                {(['VND', 'USD'] as const).map((cur) => (
                  <button
                    key={cur}
                    type="button"
                    onClick={() => setBaseCurrency(cur)}
                    className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${
                      baseCurrency === cur
                        ? 'bg-indigo-50 border-indigo-400 text-indigo-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {cur === 'VND' ? 'VND (đồng)' : 'USD (đô la)'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Huỷ</Button>
            <Button onClick={handleCreate} disabled={submitting || !name.trim()}>
              {submitting ? 'Đang tạo...' : 'Tạo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
